/** @jsxImportSource @emotion/react */
import { useRef, useState } from 'react'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Spinner from 'react-bootstrap/Spinner'
import CallOutlinedIcon from '@mui/icons-material/CallOutlined'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import PauseIcon from '@mui/icons-material/Pause'
import NorthEastIcon from '@mui/icons-material/NorthEast'
import SouthWestIcon from '@mui/icons-material/SouthWest'
import moment from 'moment'

import {
    IntermediaCallRecord,
    useGetCallHistoryQuery,
    useGetRecordingQuery,
    useGetUserRecordingsQuery,
} from 'store/services/intermedia'
import { formatPhoneNumber } from 'helpers/format'

interface Props {
  phoneNumber: string
  /** ISO date string — order completion/cancellation date; defaults to today */
  dateTo?: string
  onClose: () => void
}

const toDateInputValue = (date: Date): string => date.toISOString().slice(0, 10)

const defaultDateFrom = (): string => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return toDateInputValue(d)
}

const minDateFrom = (): string => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 3)
  return toDateInputValue(d)
}

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const fileBaseName = (path: string): string => path.split('/').pop() ?? path

const recordingSliderCss = (playedPct: number, seekable: boolean) => ({
  width: 120,
  height: 10,
  cursor: seekable ? 'pointer' : 'default',
  WebkitAppearance: 'none' as const,
  appearance: 'none' as const,
  background: 'transparent',
  '&::-webkit-slider-runnable-track': {
    height: 2,
    borderRadius: 1,
    background: `linear-gradient(to right, #000 ${playedPct}%, #dee2e6 ${playedPct}%)`,
  },
  '&::-webkit-slider-thumb': {
    WebkitAppearance: 'none' as const,
    appearance: 'none' as const,
    width: 8,
    height: 8,
    marginTop: -3,
    borderRadius: '50%',
    background: '#000',
  },
  '&::-moz-range-track': {
    height: 2,
    borderRadius: 1,
    background: '#dee2e6',
  },
  '&::-moz-range-progress': {
    height: 2,
    borderRadius: 1,
    background: '#000',
  },
  '&::-moz-range-thumb': {
    width: 8,
    height: 8,
    border: 'none',
    borderRadius: '50%',
    background: '#000',
  },
})

const DirectionBadge = ({ direction }: { direction: IntermediaCallRecord['direction'] }) => {
  if (direction === 'outbound') {
    return (
      <span css={{ display: 'flex', alignItems: 'center', gap: 4, color: '#28a745' }}>
        <NorthEastIcon fontSize="small" /> Outgoing
      </span>
    )
  }
  return (
    <span css={{ display: 'flex', alignItems: 'center', gap: 4, color: '#dc3545' }}>
      <SouthWestIcon fontSize="small" /> Incoming
    </span>
  )
}

const RecordingPlayer = ({
  userId,
  recordingId,
  fileName,
}: {
  userId: string
  recordingId: number
  fileName: string
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const shouldPlayRef = useRef(false)
  const [fetch, setFetch] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const { data: audioUrl, isFetching, isError } = useGetRecordingQuery(
    { userId, recordingId },
    { skip: !fetch },
  )

  const handleClick = () => {
    if (!fetch) {
      shouldPlayRef.current = true
      setFetch(true)
      return
    }
    if (!audioUrl || !audioRef.current) return
    if (playing) {
      audioRef.current.pause()
    } else {
      audioRef.current.play().catch(() => {})
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value)
    if (audioRef.current) audioRef.current.currentTime = t
    setCurrentTime(t)
  }

  const playedPct = duration ? (currentTime / duration) * 100 : 0

  if (isError) {
    return <span css={{ fontSize: 12, color: '#6D757E' }}>N/A</span>
  }

  return (
    <span css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button
          onClick={handleClick}
          disabled={isFetching}
          title={fileBaseName(fileName)}
          css={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: '1px solid #dee2e6',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isFetching ? 'default' : 'pointer',
            flexShrink: 0,
            '&:hover:not(:disabled)': { background: '#f0f0f0' },
          }}>
          {isFetching ? (
            <Spinner animation="border" size="sm" />
          ) : playing ? (
            <PauseIcon fontSize="small" />
          ) : (
            <PlayArrowIcon fontSize="small" />
          )}
        </button>
        <span
          css={{
            fontSize: 11,
            color: '#6D757E',
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={fileName}>
          {fileBaseName(fileName)}
        </span>
      </span>
      <span css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step="any"
          value={currentTime}
          onChange={handleSeek}
          disabled={!audioUrl || !duration}
          css={recordingSliderCss(playedPct, Boolean(audioUrl && duration))}
        />
        <span css={{ fontSize: 11, color: '#6D757E', fontVariantNumeric: 'tabular-nums' }}>
          {formatDuration(Math.floor(currentTime))} / {formatDuration(Math.floor(duration))}
        </span>
      </span>
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={() => {
            setPlaying(false)
            setCurrentTime(0)
          }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onCanPlay={(e) => {
            if (shouldPlayRef.current) {
              shouldPlayRef.current = false
              e.currentTarget.play().catch(() => {})
            }
          }}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration
            setDuration(Number.isFinite(d) ? d : 0)
          }}
          onDurationChange={(e) => {
            const d = e.currentTarget.duration
            if (Number.isFinite(d)) setDuration(d)
          }}
          onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
          css={{ display: 'none' }}
        />
      )}
    </span>
  )
}

/** Per-row recording cell with a lazy "Check" button */
const RecordingCell = ({ call }: { call: IntermediaCallRecord }) => {
  const [requested, setRequested] = useState(false)

  const userIds = [call.fromUserUniqueId, call.toUserUniqueId].filter(Boolean) as string[]

  const { data, isFetching } = useGetUserRecordingsQuery(
    { userIds, callId: call.callId },
    { skip: !requested || userIds.length === 0 },
  )

  // No internal user on either side — no recording possible
  if (call.duration === 0 || userIds.length === 0) {
    return <span css={{ fontSize: 12, color: '#6D757E' }}>N/A</span>
  }

  if (!requested) {
    return (
      <button
        onClick={() => setRequested(true)}
        css={{
          background: 'none',
          border: '1px solid #dee2e6',
          borderRadius: 4,
          padding: '2px 8px',
          fontSize: 11,
          color: '#6D757E',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { background: '#f0f0f0', borderColor: '#adb5bd' },
        }}>
        Check
      </button>
    )
  }

  if (isFetching) {
    return <Spinner animation="border" size="sm" />
  }

  const rec = data?.recordings[0]

  if (rec) {
    return (
      <RecordingPlayer
        userId={rec.userId}
        recordingId={rec.recordingId}
        fileName={rec.fileName}
      />
    )
  }

  return <span css={{ fontSize: 12, color: '#6D757E' }}>No recording</span>
}

const COLUMNS = ['DATE', 'TIME', 'DIRECTION', 'FROM', 'TO', 'DURATION', 'RECORDING']

export const CallHistoryModal = ({ phoneNumber, dateTo, onClose }: Props) => {
  const [page, setPage] = useState(1)
  const [dateFromInput, setDateFromInput] = useState(defaultDateFrom)
  const [committedDateFrom, setCommittedDateFrom] = useState(defaultDateFrom)
  const pageSize = 10

  const maxDateFrom = dateTo ? dateTo.slice(0, 10) : toDateInputValue(new Date())

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFromInput(e.target.value)
  }

  const handleDateFromBlur = () => {
    const val = dateFromInput
    const min = minDateFrom()
    const clamped = !val ? defaultDateFrom() : val < min ? min : val > maxDateFrom ? maxDateFrom : val
    setDateFromInput(clamped)
    if (clamped !== committedDateFrom) {
      setCommittedDateFrom(clamped)
      setPage(1)
    }
  }

  const { data, isFetching, isError } = useGetCallHistoryQuery(
    { phoneNumber, page, dateFrom: committedDateFrom, dateTo },
    { skip: !phoneNumber },
  )

  const totalPages = data ? Math.ceil(data.totalCalls / pageSize) : 0

  return (
    <Modal show centered size="xl" onHide={onClose}>
      <Modal.Header css={{ borderBottom: '1px solid #dee2e6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Modal.Title css={{ fontSize: 20, fontWeight: 700 }}>
          <CallOutlinedIcon css={{ marginRight: 8, verticalAlign: 'middle' }} />
          Call History
        </Modal.Title>
        <div css={{ textAlign: 'right' }}>
          <div css={{ fontSize: 16, fontWeight: 600 }}>
            {formatPhoneNumber(phoneNumber)}
          </div>
          <div css={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, justifyContent: 'flex-end' }}>
            <input
              type="date"
              value={dateFromInput}
              min={minDateFrom()}
              max={maxDateFrom}
              disabled={isFetching}
              onChange={handleDateFromChange}
              onBlur={handleDateFromBlur}
              css={{
                fontSize: 12,
                border: '1px solid #dee2e6',
                borderRadius: 4,
                padding: '2px 5px',
                color: '#495057',
                cursor: isFetching ? 'not-allowed' : 'pointer',
                opacity: isFetching ? 0.5 : 1,
                '&::-webkit-calendar-picker-indicator': { cursor: isFetching ? 'not-allowed' : 'pointer' },
              }}
            />
            <span css={{ fontSize: 13, color: '#6D757E' }}>→</span>
            <span css={{ fontSize: 13, color: '#6D757E' }}>
              {dateTo ? moment(dateTo).format('MMM D, YYYY') : 'Today'}
            </span>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body css={{ padding: 0 }}>
        {isFetching && (
          <div css={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spinner animation="border" variant="primary" />
          </div>
        )}

        {isError && (
          <div css={{ textAlign: 'center', padding: 40, color: '#dc3545' }}>
            Failed to load call history. Please check Intermedia credentials.
          </div>
        )}

        {!isFetching && !isError && data && (
          <>
            <div css={{ overflowX: 'auto' }}>
              <table css={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                <thead>
                  <tr css={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    {COLUMNS.map((col) => (
                      <th
                        key={col}
                        css={{
                          padding: '10px 14px',
                          textAlign: 'left',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#6D757E',
                          letterSpacing: '0.05em',
                          whiteSpace: 'nowrap',
                        }}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.calls.length === 0 && (
                    <tr>
                      <td colSpan={COLUMNS.length} css={{ textAlign: 'center', padding: 32, color: '#6D757E' }}>
                        No calls found for this number.
                      </td>
                    </tr>
                  )}
                  {data.calls.map((call) => (
                    <tr
                      key={call.globalCallId}
                      css={{
                        borderBottom: '1px solid #dee2e6',
                        '&:hover': { background: '#f8f9fa' },
                      }}>
                      <td css={{ padding: '10px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {moment(call.start).format('MMM D, YYYY')}
                      </td>
                      <td css={{ padding: '10px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {moment(call.start).format('h:mm A')}
                      </td>
                      <td css={{ padding: '10px 14px', fontSize: 13 }}>
                        <DirectionBadge direction={call.direction} />
                      </td>
                      <td css={{ padding: '10px 14px', fontSize: 13 }}>
                        {call.fromName && (
                          <div css={{ fontWeight: 500 }}>{call.fromName}</div>
                        )}
                        <div css={{ color: '#6D757E', fontSize: 12 }}>
                          {formatPhoneNumber(call.fromNumber)}
                        </div>
                      </td>
                      <td css={{ padding: '10px 14px', fontSize: 13 }}>
                        {call.toName && (
                          <div css={{ fontWeight: 500 }}>{call.toName}</div>
                        )}
                        <div css={{ color: '#6D757E', fontSize: 12 }}>
                          {formatPhoneNumber(call.toNumber)}
                        </div>
                      </td>
                      <td css={{ padding: '10px 14px', fontSize: 13, whiteSpace: 'nowrap' }}>
                        {formatDuration(call.duration)}
                      </td>
                      <td css={{ padding: '10px 14px' }}>
                        <RecordingCell call={call} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop: '1px solid #dee2e6',
                fontSize: 13,
                color: '#6D757E',
              }}>
              <span>Total calls: {data.totalCalls}</span>
              {totalPages > 1 && (
                <div css={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}>
                    &laquo; Prev
                  </Button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}>
                    Next &raquo;
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
